/**
 * Reclaim Our Balance - Hub Page
 * Select from available 5-day emotional reset journeys.
 * Premium minimal design with soft transitions.
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowLeft, 
  Heart, 
  Flame, 
  Cloud, 
  Wind,
  ChevronRight,
  Lock,
  Check,
  Sparkles
} from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping
const ICONS = {
  heart: Heart,
  flame: Flame,
  cloud: Cloud,
  wind: Wind
};

const BalanceHubPage = () => {
  const navigate = useNavigate();
  const [journeys, setJourneys] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const getToken = () => localStorage.getItem("reclaim_token");

  useEffect(() => {
    loadJourneys();
  }, []);

  const loadJourneys = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/balance/journeys`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setJourneys(response.data.journeys || []);
    } catch (err) {
      console.error("Error loading journeys:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <motion.div
          animate={{ scale: [1, 1.1, 1], opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <Heart size={40} className="text-violet-400" />
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      {/* Header */}
      <div className="p-6 pb-0">
        <button
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
          className="text-white/60 hover:text-white transition-colors"
          data-testid="balance-back-btn"
        >
          <ArrowLeft size={24} />
        </button>
      </div>

      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="px-6 pt-8 pb-12 text-center"
      >
        <motion.div
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring" }}
          className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-violet-500/20 to-pink-500/20 flex items-center justify-center border border-white/10"
        >
          <Sparkles size={36} className="text-violet-400" />
        </motion.div>

        <h1 className="text-3xl font-light text-white mb-3 tracking-wide">
          Reclaim Our Balance
        </h1>
        <p className="text-white/50 text-lg max-w-sm mx-auto leading-relaxed">
          5-day journeys to gently guide you back to center
        </p>
      </motion.div>

      {/* Journey Cards */}
      <div className="px-6 space-y-4 pb-12">
        {journeys.map((journey, index) => {
          const Icon = ICONS[journey.icon] || Heart;
          const isAvailable = journey.available !== false;
          const isStarted = journey.progress?.started;
          const isCompleted = journey.progress?.completed;
          
          return (
            <motion.button
              key={journey.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 + index * 0.1 }}
              onClick={() => isAvailable && navigate(`/balance/${journey.id}`)}
              disabled={!isAvailable}
              className={`w-full p-5 rounded-2xl text-left transition-all duration-300 group relative overflow-hidden ${
                isAvailable 
                  ? "hover:scale-[1.02] active:scale-[0.98]" 
                  : "opacity-50"
              }`}
              style={{
                background: isAvailable 
                  ? `linear-gradient(135deg, ${journey.gradient?.[0]}15, ${journey.gradient?.[1]}15)`
                  : "rgba(255,255,255,0.03)"
              }}
              data-testid={`balance-journey-${journey.id}`}
            >
              {/* Background glow on hover */}
              {isAvailable && (
                <div 
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{
                    background: `linear-gradient(135deg, ${journey.gradient?.[0]}10, ${journey.gradient?.[1]}10)`
                  }}
                />
              )}
              
              <div className="relative flex items-center gap-4">
                {/* Icon */}
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110"
                  style={{
                    background: isAvailable 
                      ? `linear-gradient(135deg, ${journey.gradient?.[0]}, ${journey.gradient?.[1]})`
                      : "rgba(255,255,255,0.1)"
                  }}
                >
                  {isAvailable ? (
                    <Icon size={26} className="text-white" />
                  ) : (
                    <Lock size={22} className="text-white/50" />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-medium text-lg mb-0.5">
                    {journey.title}
                  </h3>
                  <p className="text-white/40 text-sm">
                    {isAvailable ? journey.subtitle : "Coming soon..."}
                  </p>
                </div>

                {/* Status / Arrow */}
                <div className="flex-shrink-0">
                  {isCompleted ? (
                    <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                      <Check size={16} className="text-emerald-400" />
                    </div>
                  ) : isStarted ? (
                    <div className="text-right">
                      <span 
                        className="text-xs font-medium px-2 py-1 rounded-full"
                        style={{ 
                          background: `${journey.gradient?.[0]}30`,
                          color: journey.gradient?.[0]
                        }}
                      >
                        Day {journey.progress?.current_day}
                      </span>
                    </div>
                  ) : isAvailable ? (
                    <ChevronRight 
                      size={20} 
                      className="text-white/30 group-hover:text-white/60 transition-colors" 
                    />
                  ) : null}
                </div>
              </div>

              {/* Progress bar for started journeys */}
              {isStarted && !isCompleted && (
                <div className="mt-4 h-1 bg-white/5 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${(journey.progress?.current_day / journey.duration_days) * 100}%` }}
                    transition={{ delay: 0.5, duration: 0.8 }}
                    className="h-full rounded-full"
                    style={{
                      background: `linear-gradient(90deg, ${journey.gradient?.[0]}, ${journey.gradient?.[1]})`
                    }}
                  />
                </div>
              )}
            </motion.button>
          );
        })}
      </div>

      {/* Bottom wisdom */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="px-6 pb-12 text-center"
      >
        <p className="text-white/30 text-sm italic">
          "Emotions aren't problems to solve — they're doorways to walk through"
        </p>
      </motion.div>
    </div>
  );
};

export default BalanceHubPage;
